import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, TextField, MenuItem, Button, IconButton, Typography, Chip,
  CircularProgress, Snackbar, Alert, FormControl, InputLabel, Select, Tooltip,
  DialogTitle, DialogContent, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import * as XLSX from 'xlsx';
import ResponsiveTable from '../components/common/ResponsiveTable';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import apiService from '../services/api.service';

interface StockRow {
  itemCode: string; itemName: string; itemGroupCode?: number; warehouseCode: string;
  inStock: number; committed: number; ordered: number; available: number;
  /** Inventory unit of measure the quantities are expressed in. */
  uom?: string;
  manufacturerCode?: number; brandSubGrp2?: string;
  categorySubGrp1?: string; categoryCCName?: string;
  expiryDate?: string; expiryStatus?: ExpiryStatus; expiryDays?: number;
  /** True once this row's numbers were refreshed live from SAP B1 (per-row button). */
  live?: boolean;
}
type ExpiryStatus = 'expired' | 'expiring' | 'ok' | 'unknown';
interface ExpiryRow {
  itemCode: string; batch: string | null; expirationDate: string | null;
  daysUntil: number | null; status: ExpiryStatus;
}
interface ReportRow {
  itemCode: string; itemName: string; batch: string | null;
  expirationDate: string; daysUntil: number | null; status: ExpiryStatus;
}
interface WarehouseOpt { code: string; name: string; }
type CategoryField = 'ItemGroup' | 'U_Item_SubGrp1' | 'U_CCName';
interface StockFilterOptions {
  categoryField: CategoryField;
  manufacturers: Array<{ code: number; name: string }>;
  brandSubGroups: string[];
  categories: Array<{ value: string; label: string }>;
}

const fmtQty = (n: number) => Number(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 });

const pad2 = (n: number) => String(n).padStart(2, '0');
// All dates on this page are presented as DD-MM-YYYY. For ISO date strings we
// read the calendar date directly (no timezone shift); otherwise fall back to
// local date parts.
const fmtDate = (s?: string | null): string => {
  if (!s) return '—';
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '—' : `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
};
const fmtDateTime = (d: Date): string => `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()} ${d.toLocaleTimeString()}`;

// Escape values before interpolating into the export HTML string. SAP item names
// are admin-entered but can contain arbitrary characters; escaping prevents any
// HTML/script injection into the document rendered server-side by Puppeteer.
const escapeHtml = (s: string): string =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  // Defer revoke so the download has started — immediate revoke races on
  // mobile Safari / older Chromium (matches PaymentCertificate's approach).
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const EXPIRY_COLOR: Record<ExpiryRow['status'], 'error' | 'warning' | 'success' | 'default'> = {
  expired: 'error', expiring: 'warning', ok: 'success', unknown: 'default',
};

function SalesStock() {
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || undefined, []);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [warehouseCode, setWarehouseCode] = useState('');
  const [category, setCategory] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [brand2, setBrand2] = useState('');
  const [rows, setRows] = useState<StockRow[]>([]);
  const [lastRefreshed, setLastRefreshed] = useState<string>('');
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<WarehouseOpt[]>([]);
  const [filters, setFilters] = useState<StockFilterOptions>({ categoryField: 'ItemGroup', manufacturers: [], brandSubGroups: [], categories: [] });
  // code -> brand name, for the grid's Brand column.
  const mfrName = useMemo(() => {
    const m = new Map<number, string>();
    filters.manufacturers.forEach((x) => m.set(x.code, x.name));
    return m;
  }, [filters.manufacturers]);
  // group code -> label, for the Category column when categoryField = ItemGroup.
  const catLabel = useMemo(() => {
    const m = new Map<string, string>();
    filters.categories.forEach((c) => m.set(c.value, c.label));
    return m;
  }, [filters.categories]);
  const categoryDisplay = useCallback((r: StockRow): string => {
    if (filters.categoryField === 'U_Item_SubGrp1') return r.categorySubGrp1 || '—';
    if (filters.categoryField === 'U_CCName') return r.categoryCCName || '—';
    return r.itemGroupCode != null ? (catLabel.get(String(r.itemGroupCode)) || String(r.itemGroupCode)) : '—';
  }, [filters.categoryField, catLabel]);
  const brandDisplay = useCallback(
    (r: StockRow): string => (r.manufacturerCode != null ? (mfrName.get(r.manufacturerCode) || String(r.manufacturerCode)) : '—'),
    [mfrName],
  );
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const [expiry, setExpiry] = useState<{ open: boolean; itemCode: string; loading: boolean; rows: ExpiryRow[] }>(
    { open: false, itemCode: '', loading: false, rows: [] },
  );
  // Per-row "Live from SAP" state: which item codes are currently refreshing,
  // and when each was last refreshed live (for the tooltip).
  const [liveLoading, setLiveLoading] = useState<Set<string>>(new Set());
  const [liveAt, setLiveAt] = useState<Record<string, string>>({});
  // Expiry Tracking report dialog.
  const [report, setReport] = useState<{ open: boolean; loading: boolean; windowDays: number; rows: ReportRow[] }>(
    { open: false, loading: false, windowDays: 90, rows: [] },
  );

  const notify = useCallback((msg: string, sev: 'success' | 'error' = 'error') => setToast({ msg, sev }), []);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Load filter dropdowns once (warehouses + brand/category options).
  useEffect(() => {
    (async () => {
      try {
        const [wRes, fRes]: any[] = await Promise.all([
          apiService.getWarehouses(),
          companyId ? apiService.getSalesStockFilters(companyId) : Promise.resolve({ data: null }),
        ]);
        setWarehouses((wRes.data || []).map((w: any) => ({ code: w.WarehouseCode, name: w.WarehouseName })));
        if (fRes.data) {
          setFilters({
            categoryField: fRes.data.categoryField || 'ItemGroup',
            manufacturers: fRes.data.manufacturers || [],
            brandSubGroups: fRes.data.brandSubGroups || [],
            categories: fRes.data.categories || [],
          });
        }
      } catch {
        /* dropdowns are best-effort */
      }
    })();
  }, [companyId]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res: any = await apiService.getSalesStock(companyId, {
        search: debounced || undefined,
        warehouseCode: warehouseCode || undefined,
        category: category || undefined,
        manufacturer: manufacturer || undefined,
        brand2: brand2 || undefined,
      });
      setRows(res.data?.rows || []);
      setLastRefreshed(res.data?.lastRefreshed || '');
      setTruncated(!!res.data?.truncated);
    } catch (e: any) {
      notify(e.response?.data?.message || 'Failed to load stock');
    } finally {
      setLoading(false);
    }
  }, [companyId, debounced, warehouseCode, category, manufacturer, brand2, notify]);

  useEffect(() => { void load(); }, [load]);

  const openExpiry = useCallback(async (itemCode: string) => {
    if (!companyId) return;
    setExpiry({ open: true, itemCode, loading: true, rows: [] });
    try {
      const res: any = await apiService.getSalesStockExpiry(companyId, itemCode);
      setExpiry({ open: true, itemCode, loading: false, rows: res.data || [] });
    } catch (e: any) {
      setExpiry({ open: false, itemCode: '', loading: false, rows: [] });
      notify(e.response?.data?.message || 'Failed to load expiry');
    }
  }, [companyId, notify]);

  const loadReport = useCallback(async (windowDays: number) => {
    if (!companyId) return;
    setReport((p) => ({ ...p, open: true, loading: true, windowDays }));
    try {
      const res: any = await apiService.getSalesExpiryReport(companyId, { windowDays });
      setReport((p) => ({ ...p, loading: false, rows: res.data || [] }));
    } catch (e: any) {
      setReport((p) => ({ ...p, loading: false }));
      notify(e.response?.data?.message || 'Failed to load expiry report');
    }
  }, [companyId, notify]);

  const exportReportExcel = () => {
    const sheet = XLSX.utils.json_to_sheet(report.rows.map((r) => ({
      'Item Code': r.itemCode, 'Item Name': r.itemName, Batch: r.batch || '',
      Expiry: r.expirationDate ? fmtDate(r.expirationDate) : '',
      'Days to Expiry': r.daysUntil ?? '', Status: r.status,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'Expiry');
    const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    downloadBlob(new Blob([out], { type: 'application/octet-stream' }), 'expiry-report.xlsx');
  };

  const exportReportPdf = async () => {
    const body = report.rows.map((r) => `<tr>
      <td>${escapeHtml(r.itemCode)}</td><td>${escapeHtml(r.itemName)}</td><td>${escapeHtml(r.batch || '')}</td>
      <td>${r.expirationDate ? escapeHtml(fmtDate(r.expirationDate)) : ''}</td>
      <td style="text-align:right">${r.daysUntil ?? ''}</td><td>${escapeHtml(r.status)}</td></tr>`).join('');
    const html = `<html><head><meta charset="utf-8"><style>
      body{font-family:Arial,Helvetica,sans-serif;font-size:11px}
      h2{margin:0 0 4px} table{width:100%;border-collapse:collapse}
      th,td{border:1px solid #ccc;padding:4px 6px} th{background:#f0f0f0;text-align:left}
      </style></head><body><h2>Expiry Tracking Report</h2>
      <div>Within ${report.windowDays} days (and already expired) — generated ${fmtDateTime(new Date())}</div>
      <table><thead><tr><th>Item Code</th><th>Item Name</th><th>Batch</th><th>Expiry</th>
      <th style="text-align:right">Days</th><th>Status</th></tr></thead><tbody>${body}</tbody></table></body></html>`;
    try {
      const blob = await apiService.renderPdf(html, { filename: 'expiry-report.pdf', landscape: true });
      downloadBlob(blob, 'expiry-report.pdf');
    } catch (e: any) {
      notify(e.response?.data?.message || 'PDF export failed');
    }
  };

  // Refresh ONE item's stock live from SAP B1 (uncached) and merge into the
  // visible rows. A single item can occupy several warehouse rows; one click
  // updates all of them. Respects the active warehouse filter.
  const loadLive = useCallback(async (itemCode: string) => {
    if (!companyId) return;
    setLiveLoading((prev) => new Set(prev).add(itemCode));
    try {
      const res: any = await apiService.getSalesStockLive(companyId, itemCode, warehouseCode || undefined);
      const liveRows: StockRow[] = res.data?.rows || [];
      const refreshedAt: string = res.data?.refreshedAt || new Date().toISOString();
      const liveByWh = new Map(liveRows.map((r) => [r.warehouseCode, r]));
      setRows((prev) => prev.map((r) => {
        if (r.itemCode !== itemCode) return r;
        const lr = liveByWh.get(r.warehouseCode);
        return lr
          ? { ...r, inStock: lr.inStock, committed: lr.committed, ordered: lr.ordered, available: lr.available, live: true }
          : { ...r, inStock: 0, committed: 0, ordered: 0, available: 0, live: true };
      }));
      setLiveAt((prev) => ({ ...prev, [itemCode]: refreshedAt }));
    } catch (e: any) {
      notify(e.response?.data?.message || 'Live stock fetch failed');
    } finally {
      setLiveLoading((prev) => {
        const next = new Set(prev);
        next.delete(itemCode);
        return next;
      });
    }
  }, [companyId, warehouseCode, notify]);

  const exportExcel = () => {
    const sheet = XLSX.utils.json_to_sheet(rows.map((r) => ({
      'Item Code': r.itemCode, 'Item Name': r.itemName,
      Category: categoryDisplay(r), Brand: brandDisplay(r), 'Sub-Group': r.brandSubGrp2 || '',
      Warehouse: r.warehouseCode, UoM: r.uom || '',
      'In Stock': r.inStock, Committed: r.committed, Ordered: r.ordered, Available: r.available,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'Stock');
    const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    downloadBlob(new Blob([out], { type: 'application/octet-stream' }), 'stock.xlsx');
  };

  const exportPdf = async () => {
    const body = rows.map((r) => `<tr>
      <td>${escapeHtml(r.itemCode)}</td><td>${escapeHtml(r.itemName)}</td>
      <td>${escapeHtml(categoryDisplay(r))}</td><td>${escapeHtml(brandDisplay(r))}</td><td>${escapeHtml(r.brandSubGrp2 || '')}</td>
      <td>${escapeHtml(r.warehouseCode)}</td><td>${escapeHtml(r.uom || '')}</td>
      <td style="text-align:right">${fmtQty(r.inStock)}</td>
      <td style="text-align:right">${fmtQty(r.committed)}</td>
      <td style="text-align:right">${fmtQty(r.available)}</td></tr>`).join('');
    const html = `<html><head><meta charset="utf-8"><style>
      body{font-family:Arial,Helvetica,sans-serif;font-size:11px}
      h2{margin:0 0 8px} table{width:100%;border-collapse:collapse}
      th,td{border:1px solid #ccc;padding:4px 6px} th{background:#f0f0f0;text-align:left}
      </style></head><body><h2>Live Stock</h2>
      <div>Generated ${fmtDateTime(new Date())}</div>
      <table><thead><tr><th>Item Code</th><th>Item Name</th><th>Category</th><th>Brand</th><th>Sub-Group</th><th>Warehouse</th><th>UoM</th>
      <th style="text-align:right">In Stock</th><th style="text-align:right">Committed</th>
      <th style="text-align:right">Available</th></tr></thead><tbody>${body}</tbody></table></body></html>`;
    try {
      const blob = await apiService.renderPdf(html, { filename: 'stock.pdf', landscape: true });
      downloadBlob(blob, 'stock.pdf');
    } catch (e: any) {
      notify(e.response?.data?.message || 'PDF export failed');
    }
  };

  if (!companyId) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="info">Select a company (top bar) to view live stock.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
        <Typography variant="h5" component="h1">Live Stock Check</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {lastRefreshed && (
            <Typography variant="caption" color="text.secondary">
              Updated {new Date(lastRefreshed).toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Refresh"><span>
            <IconButton size="small" onClick={() => void load()} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
          </span></Tooltip>
          <Button size="small" startIcon={<EventBusyIcon />} onClick={() => void loadReport(report.windowDays)}>Expiry Report</Button>
          <Button size="small" startIcon={<GridOnIcon />} onClick={exportExcel} disabled={!rows.length}>Excel</Button>
          <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => void exportPdf()} disabled={!rows.length}>PDF</Button>
        </Stack>
      </Stack>

      <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField
            size="small" label="Search SKU or name" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="cat">Category</InputLabel>
            <Select labelId="cat" label="Category" value={category} onChange={(e) => setCategory(String(e.target.value))}>
              <MenuItem value="">All categories</MenuItem>
              {filters.categories.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="brand">Brand</InputLabel>
            <Select labelId="brand" label="Brand" value={manufacturer} onChange={(e) => setManufacturer(String(e.target.value))}>
              <MenuItem value="">All brands</MenuItem>
              {filters.manufacturers.map((m) => <MenuItem key={m.code} value={String(m.code)}>{m.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="subgrp">Sub-Group</InputLabel>
            <Select labelId="subgrp" label="Sub-Group" value={brand2} onChange={(e) => setBrand2(String(e.target.value))}>
              <MenuItem value="">All sub-groups</MenuItem>
              {filters.brandSubGroups.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="wh">Warehouse</InputLabel>
            <Select labelId="wh" label="Warehouse" value={warehouseCode} onChange={(e) => setWarehouseCode(String(e.target.value))}>
              <MenuItem value="">All warehouses</MenuItem>
              {warehouses.map((w) => <MenuItem key={w.code} value={w.code}>{w.name || w.code}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Results were truncated — narrow your search or category to see all items.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <ResponsiveTable
          ariaLabel="Live stock"
          rows={rows}
          getRowKey={(r) => `${r.itemCode}|${r.warehouseCode}`}
          emptyMessage="No stock matches the current filters."
          columns={[
            { id: 'itemCode', header: 'Item Code', cell: (r) => r.itemCode },
            { id: 'itemName', header: 'Item Name', cell: (r) => r.itemName },
            { id: 'category', header: 'Category', cell: (r) => categoryDisplay(r) },
            { id: 'brand', header: 'Brand', cell: (r) => brandDisplay(r) },
            { id: 'subGroup', header: 'Sub-Group', cell: (r) => r.brandSubGrp2 || '—' },
            { id: 'warehouseCode', header: 'Warehouse', cell: (r) => r.warehouseCode },
            { id: 'uom', header: 'UoM', cell: (r) => r.uom || '—' },
            { id: 'inStock', header: 'In Stock', align: 'right', cell: (r) => fmtQty(r.inStock) },
            { id: 'committed', header: 'Committed', align: 'right', cell: (r) => fmtQty(r.committed) },
            {
              id: 'available', header: 'Available', align: 'right',
              cell: (r) => <strong style={r.live ? { color: '#2e7d32' } : undefined}>{fmtQty(r.available)}</strong>,
            },
            {
              id: 'live', header: 'Live', align: 'center',
              cell: (r) => {
                const loading = liveLoading.has(r.itemCode);
                return (
                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                    <Tooltip title="Get live stock from SAP B1">
                      <span>
                        <IconButton size="small" color="primary" onClick={() => void loadLive(r.itemCode)} disabled={loading}>
                          {loading ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {r.live && !loading && (
                      <Tooltip title={`Live as of ${liveAt[r.itemCode] ? new Date(liveAt[r.itemCode]).toLocaleTimeString() : 'now'}`}>
                        <Chip size="small" color="success" variant="outlined" label="Live" />
                      </Tooltip>
                    )}
                  </Stack>
                );
              },
            },
            {
              id: 'expiry', header: 'Expiry', align: 'center',
              cell: (r) => {
                const label = r.expiryDate ? fmtDate(r.expiryDate) : '—';
                const color = r.expiryStatus ? EXPIRY_COLOR[r.expiryStatus] : 'default';
                return (
                  <Tooltip title="View batch expiry detail">
                    <Chip
                      size="small"
                      variant={r.expiryStatus && r.expiryStatus !== 'ok' ? 'filled' : 'outlined'}
                      color={color}
                      icon={<EventBusyIcon fontSize="small" />}
                      label={label}
                      onClick={() => void openExpiry(r.itemCode)}
                    />
                  </Tooltip>
                );
              },
            },
          ]}
        />
      )}

      <ResponsiveDialog open={expiry.open} onClose={() => setExpiry((p) => ({ ...p, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Batch expiry — {expiry.itemCode}</DialogTitle>
        <DialogContent dividers>
          {expiry.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : expiry.rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No batch / expiry data for this item.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Batch</TableCell><TableCell>Expiry</TableCell><TableCell align="right">Days</TableCell><TableCell>Status</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {expiry.rows.map((b, i) => (
                  <TableRow key={`${b.batch ?? i}`}>
                    <TableCell>{b.batch ?? '—'}</TableCell>
                    <TableCell>{b.expirationDate ? fmtDate(b.expirationDate) : '—'}</TableCell>
                    <TableCell align="right">{b.daysUntil ?? '—'}</TableCell>
                    <TableCell><Chip size="small" label={b.status} color={EXPIRY_COLOR[b.status]} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </ResponsiveDialog>

      <ResponsiveDialog open={report.open} onClose={() => setReport((p) => ({ ...p, open: false }))} maxWidth="md" fullWidth>
        <DialogTitle>Expiry Tracking Report</DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="win">Expiring within</InputLabel>
              <Select
                labelId="win" label="Expiring within" value={String(report.windowDays)}
                onChange={(e) => void loadReport(Number(e.target.value))}
              >
                <MenuItem value="30">30 days (+ expired)</MenuItem>
                <MenuItem value="60">60 days (+ expired)</MenuItem>
                <MenuItem value="90">90 days (+ expired)</MenuItem>
                <MenuItem value="180">180 days (+ expired)</MenuItem>
                <MenuItem value="365">365 days (+ expired)</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<GridOnIcon />} onClick={exportReportExcel} disabled={!report.rows.length}>Excel</Button>
            <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => void exportReportPdf()} disabled={!report.rows.length}>PDF</Button>
          </Stack>
          {report.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : report.rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No batches expiring in this window.</Typography>
          ) : (
            <Box sx={{ maxHeight: '55vh', overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Item Code</TableCell><TableCell>Item Name</TableCell><TableCell>Batch</TableCell>
                    <TableCell>Expiry</TableCell><TableCell align="right">Days</TableCell><TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.rows.map((r, i) => (
                    <TableRow key={`${r.itemCode}|${r.batch ?? i}`}>
                      <TableCell>{r.itemCode}</TableCell>
                      <TableCell>{r.itemName}</TableCell>
                      <TableCell>{r.batch ?? '—'}</TableCell>
                      <TableCell>{r.expirationDate ? fmtDate(r.expirationDate) : '—'}</TableCell>
                      <TableCell align="right">{r.daysUntil ?? '—'}</TableCell>
                      <TableCell><Chip size="small" label={r.status} color={EXPIRY_COLOR[r.status]} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
      </ResponsiveDialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

export default SalesStock;
